import { useCallback, useEffect, useMemo, useState, type ComponentType } from "react";
import { createPortal } from "react-dom";
import {
  CalendarDays,
  CheckCheck,
  ChevronRight,
  Clock,
  LayoutGrid,
  Megaphone,
  Newspaper,
  Search,
  TrendingUp,
  Trash2,
} from "lucide-react";
import {
  limparNotificacoesPainel,
  obterNotificacoesPainel,
  type AbaNotif,
  type NotificacaoItem,
  type NotificacoesPainel,
} from "../../api/notificacoes";
import { obterIconePorNome } from "../../layout/iconesDisponiveis";
import { subscribeRealtime } from "../../realtime/realtimeClient";
import { useIdioma } from "../../i18n/IdiomaProvider";
import { useConfiguracoesSistemaPublicas } from "../../utils/configuracoesSistema";
import { BotaoFechar } from "../ui/BotaoFechar";
import { CampoPesquisa } from "../ui/CampoPesquisa";
import { ChipSelecao } from "../ui/ChipSelecao";
import { EstadoVazio } from "../ui/EstadoVazio";
import { AvatarCadastro } from "../tabela/AvatarCadastro";

interface Props {
  aberto: boolean;
  onFechar: () => void;
}

const VAZIO: NotificacoesPainel = { atividades: [], novidades: [], avisos: [] };

type FiltroNotif = "todas" | "naoLidas" | "hoje";

type IconeChip = ComponentType<{ size?: string | number }>;

const ABAS: Array<{ chave: AbaNotif; rotulo: string; Icone: IconeChip }> = [
  { chave: "atividades", rotulo: "Atividades", Icone: TrendingUp },
  { chave: "novidades", rotulo: "Novidades", Icone: Newspaper },
  { chave: "avisos", rotulo: "Avisos", Icone: Megaphone },
];

const FILTROS: Array<{ chave: FiltroNotif; rotulo: string; Icone: IconeChip }> = [
  { chave: "todas", rotulo: "Todas", Icone: LayoutGrid },
  { chave: "naoLidas", rotulo: "Não lidas", Icone: Circulo },
  { chave: "hoje", rotulo: "Hoje", Icone: CalendarDays },
];

const PLACEHOLDERS: Record<AbaNotif, string> = {
  atividades: "Pesquisar atividades",
  novidades: "Pesquisar novidades",
  avisos: "Pesquisar avisos",
};

/** Bolinha usada no filtro "Não lidas" (o lucide não tem um ponto sólido pequeno). */
function Circulo(props: { size?: string | number }) {
  const tamanho = props.size ?? 14;
  return (
    <svg viewBox="0 0 12 12" width={tamanho} height={tamanho} aria-hidden="true">
      <circle cx="6" cy="6" r="3.4" fill="currentColor" />
    </svg>
  );
}

const CHAVE_LIDAS = "notificacoesLidas";
const MAX_LIDAS_GUARDADAS = 500;

function lerLidas(): Set<string> {
  try {
    const bruto = localStorage.getItem(CHAVE_LIDAS);
    const lista: unknown = bruto ? JSON.parse(bruto) : [];
    return new Set(Array.isArray(lista) ? lista.map(String) : []);
  } catch {
    return new Set();
  }
}

function gravarLidas(lidas: Set<string>) {
  try {
    localStorage.setItem(
      CHAVE_LIDAS,
      JSON.stringify(Array.from(lidas).slice(-MAX_LIDAS_GUARDADAS))
    );
  } catch {
    // Sem localStorage (modo privado/quota): o estado vale só para esta sessão.
  }
}

function chaveItem(aba: AbaNotif, item: NotificacaoItem, index: number): string {
  const identificador = item.id ?? `${item.titulo ?? ""}|${item.tempo ?? ""}|${index}`;
  return `${aba}:${identificador}`;
}

function normalizarBusca(texto: string): string {
  return texto
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/** O back-end devolve `tempo` já formatado (dd/mm/aaaa [hh:mm]) ou em texto relativo. */
function extrairData(tempo?: string): Date | null {
  const encontrado = String(tempo ?? "").match(/(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?/);
  if (!encontrado) return null;
  const [, dia, mes, ano, hora, minuto] = encontrado;
  return new Date(Number(ano), Number(mes) - 1, Number(dia), Number(hora ?? 0), Number(minuto ?? 0));
}

function ehDeHoje(tempo?: string): boolean {
  const data = extrairData(tempo);
  if (data) {
    const hoje = new Date();
    return (
      data.getFullYear() === hoje.getFullYear() &&
      data.getMonth() === hoje.getMonth() &&
      data.getDate() === hoje.getDate()
    );
  }
  return /agora|hoje|minuto|segundo|hora/i.test(String(tempo ?? ""));
}

function temHorario(tempo?: string): boolean {
  return /\d{1,2}:\d{2}/.test(String(tempo ?? ""));
}

export function CentralNotificacoes({ aberto, onFechar }: Props) {
  const { traduzir } = useIdioma();
  const [dados, setDados] = useState<NotificacoesPainel>(VAZIO);
  const [aba, setAba] = useState<AbaNotif>("atividades");
  const [filtro, setFiltro] = useState<FiltroNotif>("todas");
  const [busca, setBusca] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [limpando, setLimpando] = useState(false);
  const [lidas, setLidas] = useState<Set<string>>(() => lerLidas());
  const [expandido, setExpandido] = useState("");

  const carregarPainel = useCallback(async () => {
    setCarregando(true);
    try {
      const resposta = await obterNotificacoesPainel();
      setDados(resposta);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    if (!aberto) return;
    setBusca("");
    setLidas(lerLidas());
    void carregarPainel();
    return subscribeRealtime(["novaMensagemAtendimento"], () => void carregarPainel());
  }, [aberto, carregarPainel]);

  useEffect(() => {
    if (!aberto) return;
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === "Escape") onFechar();
    };
    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  }, [aberto, onFechar]);

  useEffect(() => {
    if (!aberto) return;
    setBusca("");
    setFiltro("todas");
    setExpandido("");
  }, [aba, aberto]);

  const itensDaAba = useMemo(
    () =>
      (dados[aba] ?? []).map((item, index) => ({
        item,
        chave: chaveItem(aba, item, index),
      })),
    [aba, dados]
  );

  const lista = useMemo(() => {
    const termo = normalizarBusca(busca.trim());
    return itensDaAba.filter(({ item, chave }) => {
      if (filtro === "naoLidas" && lidas.has(chave)) return false;
      if (filtro === "hoje" && !ehDeHoje(item.tempo)) return false;
      if (!termo) return true;
      return normalizarBusca(`${item.titulo ?? ""} ${item.texto ?? ""}`).includes(termo);
    });
  }, [itensDaAba, busca, filtro, lidas]);

  const contagem = useCallback((chave: AbaNotif) => dados[chave]?.length ?? 0, [dados]);
  const naoLidasDaAba = itensDaAba.filter(({ chave }) => !lidas.has(chave)).length;
  const podeLimpar = aba === "atividades" && contagem("atividades") > 0 && !limpando;

  function marcarLida(chave: string) {
    setLidas((atual) => {
      if (atual.has(chave)) return atual;
      const proximas = new Set(atual);
      proximas.add(chave);
      gravarLidas(proximas);
      return proximas;
    });
  }

  function marcarTodasLidas() {
    setLidas((atual) => {
      const proximas = new Set(atual);
      itensDaAba.forEach(({ chave }) => proximas.add(chave));
      gravarLidas(proximas);
      return proximas;
    });
  }

  function alternarItem(chave: string) {
    setExpandido((atual) => (atual === chave ? "" : chave));
    marcarLida(chave);
  }

  async function limparTudo() {
    if (!podeLimpar) return;
    setLimpando(true);
    try {
      const sucesso = await limparNotificacoesPainel();
      if (sucesso) setDados((atual) => ({ ...atual, atividades: [] }));
    } finally {
      setLimpando(false);
    }
  }

  function renderItem({ item, chave }: { item: NotificacaoItem; chave: string }) {
    const IconeTela = obterIconePorNome(item.icone);
    const IconeAba = ABAS.find((opcao) => opcao.chave === aba)?.Icone;
    // No modo compacto sempre mostramos um ícone (o do módulo, ou o da aba como fallback) em vez da bolinha com iniciais.
    /* O avatar do item mostra o ícone da tela (ou o da aba) nos dois modos —
       a bolinha com iniciais só sobra quando não há ícone nenhum. */
    const IconeFallback = IconeAba ?? LayoutGrid;
    const naoLida = !lidas.has(chave);
    const aberta = expandido === chave;
    const IconeTempo = temHorario(item.tempo) ? Clock : CalendarDays;
    return (
      <article
        key={chave}
        className={[
          "notifItem",
          naoLida ? "notifItemNaoLida" : "",
          aberta ? "notifItemAberta" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <button
          type="button"
          className="notifItemLinha"
          aria-expanded={aberta}
          onClick={() => alternarItem(chave)}
        >
          <span className="notifItemPonto" aria-hidden="true" />
          {aba === "atividades" ? (
            <AvatarCadastro
              nome={item.usuarioNome || item.titulo || "?"}
              foto={item.avatarUrl}
              tamanho={38}
              className="notifItemAvatarUsuario"
            />
          ) : (
            <span className="notifItemAvatar">
              {IconeTela ? <IconeTela width={18} height={18} /> : <IconeFallback size={18} />}
            </span>
          )}
          <span className="notifItemTextos">
            <strong className="notifItemTitulo" data-i18n-db="true">{item.titulo ?? "Notificação"}</strong>
            {item.texto && <span className="notifItemResumo" data-i18n-db="true">{item.texto}</span>}
          </span>
          {item.tempo && (
            <span className="notifItemTempo">
              <IconeTempo size={12} strokeWidth={1.9} aria-hidden="true" />
              {item.tempo}
            </span>
          )}
          <span className="notifItemSeta" aria-hidden="true">
            <ChevronRight size={16} strokeWidth={2} />
          </span>
        </button>
        {aberta && item.texto && <div className="notifItemDetalhe">{item.texto}</div>}
      </article>
    );
  }

  return createPortal(
    <>
      {aberto && <div className="notifOverlay" onClick={onFechar} />}
      <aside className={`notifPanel${aberto ? " open" : ""}`} data-tour="notifications-panel" aria-hidden={!aberto}>
        <div className="notifHeader">
          <div className="notifAvatar" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.7 21a2 2 0 0 1-3.4 0" />
            </svg>
          </div>
          <div className="notifHeaderText">
            <div className="notifTitle">{traduzir("Notificações")}</div>
            <div className="notifSubtitle">{traduzir("Acompanhe seus alertas em tempo real")}</div>
          </div>
          <BotaoFechar className="notifClose" onClick={onFechar} />
        </div>

        <CampoPesquisa
          className="notifSearch"
          inputClassName="notifSearchInput"
          type="text"
          placeholder={PLACEHOLDERS[aba]}
          value={busca}
          onChange={(evento) => setBusca(evento.target.value)}
          iconeInicio={<Search size={16} strokeWidth={1.9} aria-hidden="true" />}
        />

        <div className="notifFiltros">
          <div className="notifFiltrosChips" role="group" aria-label={traduzir("Filtrar notificações")}>
            {FILTROS.map((opcao) => (
              <ChipSelecao
                key={opcao.chave}
                selecionado={filtro === opcao.chave}
                className="notifFiltroChip"
                iconeInicio={<opcao.Icone size={14} />}
                onClick={() => setFiltro(opcao.chave)}
              >
                {opcao.rotulo}
              </ChipSelecao>
            ))}
          </div>
          <button
            type="button"
            className="notifFiltroAcao tooltip tooltipUp"
            data-tooltip={traduzir("Marcar todas como lidas")}
            aria-label={traduzir("Marcar todas como lidas")}
            disabled={naoLidasDaAba === 0}
            onClick={marcarTodasLidas}
          >
            <CheckCheck size={17} strokeWidth={1.9} />
          </button>
        </div>

        <div className="notifList">
          {carregando ? (
            <div className="notifLoading">{traduzir("Carregando...")}</div>
          ) : lista.length === 0 ? (
            (
              <div className="notifEmpty">{traduzir("Nenhuma notificação por aqui.")}</div>
            )
          ) : (
            lista.map(renderItem)
          )}
        </div>

        <div className="notifFilterBar">
          <div className="notifFilterTabs">
            {ABAS.map((item) => (
              <ChipSelecao
                key={item.chave}
                selecionado={aba === item.chave}
                className={`notifFilterBtn${aba === item.chave ? " is-active" : ""}`}
                iconeInicio={<item.Icone size={14} />}
                onClick={() => setAba(item.chave)}
              >
                <>
                  {traduzir(item.rotulo)}
                  <span className={`notifTabBadge${contagem(item.chave) === 0 ? " hidden" : ""}`}>
                    {contagem(item.chave)}
                  </span>
                </>
              </ChipSelecao>
            ))}
          </div>
          <button
            className="notifClearBtn"
            type="button"
            disabled={!podeLimpar}
            onClick={() => void limparTudo()}
          >
            <Trash2 size={14} strokeWidth={1.9} aria-hidden="true" />
            {traduzir("Limpar tudo")}
          </button>
        </div>
      </aside>
    </>,
    document.body
  );
}
